import { useDeferredValue, useMemo } from 'react';

import { mapDataSeriesToChartContent } from '../api/chart-model/timeSeriesChartMappers';
import { useDataSeriesQuery } from '../api/data-series/useDataSeriesQuery';
import type { DiagramSeriesOption } from '../types/DiagramSeries.types';
import type { TimeSeriesChartModel } from '../types/TimeSeriesChartModel.types';
import type { TimeWindowMicros } from '../types/TimeWindow.types';
import { getChartHighlight, getPreviewSeriesId } from '../utils/chartHighlight';
import { getTimeSeriesChartStatus } from '../utils/getTimeSeriesChartStatus';
import { DISABLED_TIME_WINDOW } from '../utils/timeWindow';

type UseTimeSeriesChartModelParams = {
  targetId: number | undefined;
  timeWindow: TimeWindowMicros | undefined;
  /** The remembered selection, requested at once instead of waiting for the series list to validate it. */
  requestedSeriesIds: string[];
  /** Every series of the target in the window; see useDiagramSeriesOptions. */
  series: DiagramSeriesOption[];
  /** The part of the selection that is valid for the target; see useDiagramSeriesOptions. */
  selectedSeriesIds: string[];
  highlightedSeriesIds: string[];
  getSeriesColor: (seriesId: string) => string;
};

/** What the chart draws: the samples of the selected series, the highlight on top, and the result status. */
export function useTimeSeriesChartModel({
  targetId,
  timeWindow,
  requestedSeriesIds,
  series,
  selectedSeriesIds,
  highlightedSeriesIds,
  getSeriesColor,
}: UseTimeSeriesChartModelParams) {
  const dataSeriesQuery = useDataSeriesQuery({ targetId, timeWindow, selectedSeriesIds: requestedSeriesIds });
  const dataSeries = dataSeriesQuery.data;

  // Deferred, so a click in the selector paints before every line is redrawn.
  const chartSelectedSeriesIds = useDeferredValue(selectedSeriesIds);
  const chartHighlightedSeriesIds = useDeferredValue(highlightedSeriesIds);

  // Two memos on purpose: the content walks every sample, the highlight only touches ids. Keeping the
  // content's references across a highlight toggle lets the chart reuse the already parsed series.
  const chartContent = useMemo(
    () =>
      mapDataSeriesToChartContent({
        metadata: series,
        selectedSeriesIds: chartSelectedSeriesIds,
        dataSeries: dataSeries ?? [],
        getSeriesColor,
        timeWindow: timeWindow ?? DISABLED_TIME_WINDOW,
      }),
    [series, chartSelectedSeriesIds, dataSeries, getSeriesColor, timeWindow]
  );

  const highlight = useMemo(
    () => getChartHighlight(chartContent, chartHighlightedSeriesIds),
    [chartContent, chartHighlightedSeriesIds]
  );

  const chart = useMemo<TimeSeriesChartModel>(() => ({ ...chartContent, highlight }), [chartContent, highlight]);

  // Both interaction lists already carry the most-recent id last; see getPreviewSeriesId.
  const previewSeriesId = useMemo(
    () => getPreviewSeriesId(chartContent.series, chartHighlightedSeriesIds, chartSelectedSeriesIds),
    [chartContent.series, chartHighlightedSeriesIds, chartSelectedSeriesIds]
  );

  const status = getTimeSeriesChartStatus({
    hasSelection: targetId !== undefined && timeWindow !== undefined && chartSelectedSeriesIds.length > 0,
    hasData: dataSeries !== undefined,
    isPending: dataSeriesQuery.isPending,
    // Previous data shown as a placeholder means newer data is on its way.
    isFetching: dataSeriesQuery.isFetching || dataSeriesQuery.isPlaceholderData,
    isError: dataSeriesQuery.isError,
  });

  function retry() {
    void dataSeriesQuery.refetch();
  }

  return { chart, previewSeriesId, status, retry };
}
